const tiles = [
  {
    id: '01',
    icon: 'ri-shield-cross-line',
    headline: 'Blacklist Immunity',
    subline: 'Institutional Trust',
    desc: 'Ensure your liquidity is not flagged by institutional exchanges, OTC desks, or regulated custodians. A single interaction with a tainted address — even unknowingly — triggers automatic account freezes at compliance-grade platforms. The BPI Certificate provides documented clearance before any transaction is executed.',
    bullets: [
      'Exchange-level freeze prevention',
      'Tainted wallet contamination detection',
      'Pre-transaction due diligence clearance',
      'Institutional reputation protection',
    ],
    theme: 'light',
  },
  {
    id: '02',
    icon: 'ri-code-box-line',
    headline: 'Contract Sanitization',
    subline: 'Contract Integrity',
    desc: 'Detect and expose malicious "Freeze" or "Burn" functions embedded in unverified USDT/USDC smart contracts. These hidden administrative controls are invisible in standard wallet interfaces but fully extractable at bytecode level. BPI\'s proprietary engine surfaces every dangerous method before you transact.',
    bullets: [
      'Hidden freeze function detection',
      'Burn & confiscation mechanism audit',
      'Proxy contract upgrade risk assessment',
      'Honey pot logic identification',
    ],
    theme: 'dark',
  },
  {
    id: '03',
    icon: 'ri-verified-badge-line',
    headline: 'Verified Status',
    subline: 'Asset Authenticity',
    desc: 'Authenticate the asset against the official Tether and Circle issuance nodes. Counterfeit stablecoins deploying the USDT/USDC name and interface but lacking canonical contract signatures are immediately flagged. Our audit cross-references every submission against 50+ global compliance databases including FATF, FINMA, and OFAC.',
    bullets: [
      'Official Tether / Circle node verification',
      'OFAC SDN & FINMA watchlist screening',
      'Counterfeit contract identification',
      '50+ proprietary forensics databases',
    ],
    theme: 'light',
  },
  {
    id: '04',
    icon: 'ri-timer-flash-line',
    headline: 'Audit Duration',
    subline: 'Processing Guarantee',
    desc: 'Guaranteed 5-minute automated processing with instant PDF certificate issuance upon completion. From wallet connection to signed BPI Compliance Certificate — in under five minutes. No manual review queues, no waiting periods. Swiss-certified institutional output, delivered at machine speed.',
    bullets: [
      'Guaranteed 5-minute automated audit',
      'Instant PDF certificate delivery',
      'Optional on-chain attestation metadata',
      '90-day validity, court-admissible',
    ],
    theme: 'dark',
  },
];

export default function WhyVerify() {
  return (
    <section id="why-verify" className="bg-slate-50 py-16 sm:py-24 px-5 sm:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">

        {/* Section header */}
        <div className="mb-12 sm:mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-1 h-8 bg-emerald-500 rounded-full"></div>
            <span
              className="text-slate-500 text-xs font-bold tracking-[0.2em] uppercase"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              The Value of the BPI Compliance Certificate
            </span>
          </div>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <h2
              className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 leading-[1.08] tracking-tight max-w-2xl"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              Why Every Institution
              <br />
              <span className="text-emerald-600">Must Verify.</span>
            </h2>
            <p
              className="text-slate-500 text-base leading-relaxed max-w-md"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              In the ERC-20 ecosystem, unverified stablecoins carry invisible legal and financial
              risks. A single BPI audit eliminates each of them in 5 minutes.
            </p>
          </div>
        </div>

        {/* Tiles grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 mb-10 sm:mb-14">
          {tiles.map((tile) => (
            <div
              key={tile.id}
              className={`rounded-xl sm:rounded-2xl p-7 sm:p-10 relative overflow-hidden ${
                tile.theme === 'dark'
                  ? 'bg-[#001529] text-white'
                  : 'bg-white border border-slate-200 text-slate-900'
              }`}
            >
              {/* Number watermark */}
              <div
                className={`absolute -bottom-3 -right-1 text-8xl font-black select-none pointer-events-none leading-none ${
                  tile.theme === 'dark' ? 'text-white/[0.04]' : 'text-slate-900/[0.04]'
                }`}
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {tile.id}
              </div>

              <div className="relative z-10">
                {/* Top row */}
                <div className="flex items-start justify-between gap-4 mb-6">
                  <div
                    className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      tile.theme === 'dark' ? 'bg-white/10' : 'bg-emerald-50'
                    }`}
                  >
                    <i
                      className={`${tile.icon} text-2xl ${
                        tile.theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                      }`}
                    ></i>
                  </div>
                  <span
                    className={`text-xs font-bold tracking-[0.15em] uppercase mt-1 ${
                      tile.theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                    }`}
                    style={{ fontFamily: 'Inter, sans-serif' }}
                  >
                    {tile.subline}
                  </span>
                </div>

                {/* Headline */}
                <h3
                  className={`text-xl sm:text-2xl font-black mb-3 leading-snug ${
                    tile.theme === 'dark' ? 'text-white' : 'text-slate-900'
                  }`}
                  style={{ fontFamily: 'Montserrat, sans-serif' }}
                >
                  {tile.headline}
                </h3>

                {/* Description */}
                <p
                  className={`text-sm sm:text-base leading-relaxed mb-6 ${
                    tile.theme === 'dark' ? 'text-slate-400' : 'text-slate-500'
                  }`}
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {tile.desc}
                </p>

                {/* Bullets */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {tile.bullets.map((bullet, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <i
                        className={`ri-check-line text-xs flex-shrink-0 ${
                          tile.theme === 'dark' ? 'text-emerald-400' : 'text-emerald-600'
                        }`}
                      ></i>
                      <span
                        className={`text-xs font-medium ${
                          tile.theme === 'dark' ? 'text-slate-300' : 'text-slate-600'
                        }`}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                      >
                        {bullet}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="bg-[#001529] rounded-2xl p-7 sm:p-12 flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-10">
          <div className="flex items-center gap-5">
            <div className="w-14 h-14 bg-emerald-500/15 rounded-xl flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-fill text-3xl text-emerald-400"></i>
            </div>
            <div>
              <div
                className="text-white font-black text-lg sm:text-xl leading-tight mb-1"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                Don&apos;t Let Unverified Assets Compromise Your Institution
              </div>
              <div className="text-slate-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>
                Every unverified USDT/USDC transaction is a liability. The BPI Certificate
                transforms compliance risk into certainty — in 5 minutes, at no cost.
              </div>
            </div>
          </div>
          <a
            href="https://mycryptoaml.com/verification"
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 flex items-center gap-2.5 bg-emerald-500 hover:bg-emerald-400 text-white px-8 py-4 rounded-lg font-black text-sm sm:text-base transition-all duration-200 whitespace-nowrap cursor-pointer"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            <i className="ri-arrow-right-circle-fill text-lg"></i>
            Proceed to Verification
          </a>
        </div>
      </div>
    </section>
  );
}
